import { useEffect, useRef, useState } from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { CompoundSwitcher } from "@/components/CompoundSwitcher";
import { EditDoseSheet } from "@/components/EditDoseSheet";
import { AddDoseSheet } from "@/components/AddDoseSheet";
import { useCompounds } from "@/lib/state/compounds";
import {
  formatMg,
  formatShortDateTime,
  formatUnitsShort,
  mgToSyringeUnits,
} from "@/lib/format";
import { cn } from "@/lib/utils/cn";
import type { LoggedDose, DoseStatus } from "@/lib/state/compounds";

export const Route = createFileRoute("/_tabs/log")({
  component: LogScreen,
});

type Filter = "all" | DoseStatus;
const FILTERS: { value: Filter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "confirmed", label: "Confirmed" },
  { value: "pending", label: "Pending" },
  { value: "skipped", label: "Skipped" },
];

function LogScreen() {
  const { active, view, updateDose, deleteDose, createDose } = useCompounds();
  const [filter, setFilter] = useState<Filter>("all");
  const [editing, setEditing] = useState<LoggedDose | null>(null);
  const [adding, setAdding] = useState(false);

  const allDoses: LoggedDose[] = [
    ...(view?.confirmedDoses ?? []),
    ...(view?.pendingDoses ?? []),
    ...(view?.skippedDoses ?? []),
  ].sort((a, b) => a.takenAt.getTime() - b.takenAt.getTime());

  const visible =
    filter === "all" ? allDoses : allDoses.filter((d) => d.status === filter);

  const scrollRef = useRef<ScrollView>(null);
  useEffect(() => {
    const t = setTimeout(
      () => scrollRef.current?.scrollToEnd({ animated: false }),
      60,
    );
    return () => clearTimeout(t);
  }, [active?.id, filter, visible.length]);

  if (!active || !view) return null;

  return (
    <SafeAreaView className="flex-1 bg-bg" edges={["top"]}>
      <View className="flex-row items-center justify-between px-5 pb-3 pt-2">
        <View className="flex-1">
          <Text className="text-xl font-semibold text-text">Log</Text>
          <Text className="mt-0.5 text-xs text-textMuted">
            {active.preset.name} · {allDoses.length}{" "}
            {allDoses.length === 1 ? "entry" : "entries"}
          </Text>
        </View>
        <View className="flex-row items-center gap-2">
          <Pressable
            onPress={() => setAdding(true)}
            className="flex-row items-center gap-1 rounded-full border border-border bg-surface px-3 py-1.5 active:opacity-70"
            hitSlop={6}
          >
            <Plus color="#18181B" size={14} />
            <Text className="text-xs font-medium text-text">Add dose</Text>
          </Pressable>
          <CompoundSwitcher />
        </View>
      </View>

      <View className="flex-row gap-1.5 px-5 pb-3">
        {FILTERS.map((f) => {
          const isOn = filter === f.value;
          return (
            <Pressable
              key={f.value}
              onPress={() => setFilter(f.value)}
              className={cn(
                "rounded-full px-3 py-1.5 active:opacity-70",
                isOn ? "bg-text" : "bg-surfaceHi",
              )}
            >
              <Text
                className={cn(
                  "text-xs font-medium",
                  isOn ? "text-white" : "text-textDim",
                )}
              >
                {f.label}
              </Text>
            </Pressable>
          );
        })}
      </View>

      <ScrollView
        ref={scrollRef}
        className="flex-1"
        contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 48 }}
        showsVerticalScrollIndicator={false}
      >
        {visible.length === 0 ? (
          <Text className="py-12 text-center text-sm text-textMuted">
            No {filter === "all" ? "" : filter} doses for{" "}
            {active.preset.shortCode}.
          </Text>
        ) : (
          visible.map((d) => {
            const units = mgToSyringeUnits(
              d.amount,
              active.concentrationMgPerMl,
            );
            const delta =
              ((d.amount - active.schedule.defaultDoseMg) /
                active.schedule.defaultDoseMg) *
              100;
            const deltaLabel =
              Math.abs(delta) < 2
                ? null
                : `${delta > 0 ? "+" : ""}${delta.toFixed(0)}%`;
            const deltaColor = delta > 0 ? "text-warning" : "text-success";
            const isSkipped = d.status === "skipped";

            return (
              <Pressable
                key={d.id}
                onPress={() => setEditing(d)}
                className="flex-row items-center gap-3 border-b border-border/60 py-3.5 active:bg-surfaceHi/50"
              >
                <StatusDot status={d.status} />
                <View className="flex-1">
                  <View className="flex-row items-baseline gap-1.5">
                    <Text
                      className={cn(
                        "text-[17px] font-medium",
                        isSkipped ? "text-textMuted line-through" : "text-text",
                      )}
                    >
                      {formatUnitsShort(units)}
                      <Text className="text-sm font-normal text-textMuted">
                        u
                      </Text>
                    </Text>
                    <Text className="text-sm text-textMuted">·</Text>
                    <Text
                      className={cn(
                        "text-[17px] font-medium",
                        isSkipped ? "text-textMuted line-through" : "text-text",
                      )}
                    >
                      {formatMg(d.amount)}
                    </Text>
                    {deltaLabel && !isSkipped && (
                      <Text className={cn("ml-1 text-xs", deltaColor)}>
                        {deltaLabel}
                      </Text>
                    )}
                  </View>
                  <Text className="mt-0.5 text-[11px] text-textMuted">
                    {formatShortDateTime(d.takenAt)}
                  </Text>
                </View>
                <StatusPill status={d.status} />
              </Pressable>
            );
          })
        )}
      </ScrollView>

      <EditDoseSheet
        visible={editing !== null}
        dose={editing}
        concentrationMgPerMl={active.concentrationMgPerMl}
        onClose={() => setEditing(null)}
        onSave={(patch) => {
          if (editing) return updateDose(editing.id, patch);
        }}
        onDelete={() => {
          if (editing) return deleteDose(editing.id);
        }}
      />

      <AddDoseSheet
        visible={adding}
        defaultAmountMg={active.schedule.defaultDoseMg}
        concentrationMgPerMl={active.concentrationMgPerMl}
        onClose={() => setAdding(false)}
        onCreate={createDose}
      />
    </SafeAreaView>
  );
}

function StatusDot({ status }: { status: DoseStatus }) {
  if (status === "confirmed")
    return <View className="h-2 w-2 rounded-full bg-success" />;
  if (status === "pending")
    return <View className="h-2 w-2 rounded-full bg-accent" />;
  return <View className="h-2 w-2 rounded-full border border-borderHi bg-bg" />;
}

function StatusPill({ status }: { status: DoseStatus }) {
  const palette = {
    confirmed: "text-success",
    pending: "text-accent",
    skipped: "text-textMuted",
  }[status];
  return (
    <Text
      className={cn(
        "text-[10px] font-medium uppercase tracking-wider",
        palette,
      )}
    >
      {status}
    </Text>
  );
}
